declare const VALUE: unique symbol;

/**
 * A dotted path that remembers what it holds. At runtime it is the plain string it always was, so
 * a module that has not been rewritten yet can still pass a literal: the value type is then unknown.
 * The paths themselves are enumerated in keys.ts.
 */
export type StoreKey<V> = string & { readonly [VALUE]?: V };

export const storeKey = <V>(path: string): StoreKey<V> => path as StoreKey<V>;

/** What a subscriber is told: which key changed, and to what. */
export interface StoreEvent<V = unknown> {
    key: string;
    value?: V;
    oldValue?: V;
    batch?: boolean;
}

export type Unsubscribe = () => void;

export interface Store {
    /** The whole state, cloned. Throws if anything in it is not serializable, which is the point. */
    get(): Record<string, unknown>;
    get<V>(key: StoreKey<V>): V;
    set<V>(key: StoreKey<V>, value: V): void;
    subscribe<V>(key: StoreKey<V>, callback: (event: StoreEvent<V>) => void): Unsubscribe;
    batch(updater: (set: <V>(key: StoreKey<V>, value: V) => void) => void): void;
}

// The state is a tree of slices walked by string path, so the walk itself is untyped by nature.
// Types live at the edges: what callers ask for, and what subscribers are handed.
type StateTree = Record<string, any>;

/**
 * Creates a Redux-like store with hierarchical subscriptions.
 *
 * @param initialState - Initial state organized by slices
 */
export function createStore(initialState: StateTree): Store {
    const state: StateTree = structuredClone(initialState);
    const subscribers = new Map<string, Set<(event: StoreEvent<any>) => void>>();
    let batchDepth = 0;
    const pendingNotifications = new Set<string>();

    /**
     * Get a value from the store.
     * @param key - Dot-notation key (e.g., 'user.email') or undefined for all state
     */
    function get<V>(key?: StoreKey<V>): V {
        if (key === undefined) {
            return structuredClone(state) as V;
        }
        const parts = key.split('.');
        let value = state;
        for (const part of parts) {
            if (value === undefined || value === null) {
                return undefined as V;
            }
            value = value[part];
        }
        return value as V;
    }

    /** Set a value in the store and notify subscribers. */
    function set<V>(key: StoreKey<V>, value: V): void {
        const parts = key.split('.');
        let current = state;

        // Navigate to parent of target
        for (let i = 0; i < parts.length - 1; i++) {
            const part = parts[i]!;
            if (current[part] === undefined) {
                current[part] = {};
            }
            current = current[part];
        }

        const finalKey = parts[parts.length - 1]!;
        const oldValue = current[finalKey];

        // Only update if value changed
        if (oldValue === value) {
            return;
        }

        current[finalKey] = value;

        // Collect keys to notify (property, slice, and global)
        const keysToNotify = [key];

        // Add parent slice keys
        let parentKey = '';
        for (let i = 0; i < parts.length - 1; i++) {
            parentKey = parentKey ? `${parentKey}.${parts[i]}` : parts[i]!;
            keysToNotify.push(parentKey);
        }

        // Add global wildcard
        keysToNotify.push('*');

        if (batchDepth > 0) {
            // In batch mode, queue notifications
            for (const k of keysToNotify) {
                pendingNotifications.add(k);
            }
        } else {
            // Immediate notification
            notifySubscribers(keysToNotify, key, value, oldValue);
        }
    }

    /** Notify all subscribers for the given keys. */
    function notifySubscribers(keys: string[], changedKey: string, newValue: unknown, oldValue: unknown): void {
        const notified = new Set<(event: StoreEvent<any>) => void>();
        for (const key of keys) {
            const subs = subscribers.get(key);
            if (subs) {
                for (const cb of subs) {
                    if (!notified.has(cb)) {
                        notified.add(cb);
                        try {
                            cb({ key: changedKey, value: newValue, oldValue });
                        } catch (e) {
                            console.error(`Store subscriber error for key '${key}':`, e);
                        }
                    }
                }
            }
        }
    }

    /**
     * Subscribe to state changes.
     * @param key - Key to subscribe to ('user.email', 'user', or '*' for all)
     * @param callback - Called with { key, value, oldValue }
     * @returns Unsubscribe function
     */
    function subscribe<V>(key: StoreKey<V>, callback: (event: StoreEvent<V>) => void): Unsubscribe {
        if (!subscribers.has(key)) {
            subscribers.set(key, new Set());
        }
        subscribers.get(key)!.add(callback);

        return function unsubscribe() {
            const subs = subscribers.get(key);
            if (subs) {
                subs.delete(callback);
                if (subs.size === 0) {
                    subscribers.delete(key);
                }
            }
        };
    }

    /**
     * Batch multiple updates and notify subscribers once at the end.
     * @param updater - Function that receives set() and performs updates
     */
    function batch(updater: (set: <V>(key: StoreKey<V>, value: V) => void) => void): void {
        batchDepth++;
        try {
            updater(<V>(key: StoreKey<V>, value: V) => set(key, value));
        } finally {
            batchDepth--;
            if (batchDepth === 0 && pendingNotifications.size > 0) {
                const keys = Array.from(pendingNotifications);
                pendingNotifications.clear();
                // Notify once for each unique key
                const notified = new Set<(event: StoreEvent<any>) => void>();
                for (const key of keys) {
                    const subs = subscribers.get(key);
                    if (subs) {
                        for (const cb of subs) {
                            if (!notified.has(cb)) {
                                notified.add(cb);
                                try {
                                    cb({ key, batch: true });
                                } catch (e) {
                                    console.error(`Store batch subscriber error for key '${key}':`, e);
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    return {
        get,
        set,
        subscribe,
        batch,
    };
}
